// Ai đang đọc hướng dẫn — nhóm theo CÔNG VIỆC, không theo cấp bậc.
//
// CEO chốt 01/09/2026 (câu 2, phương án a): mỗi vai đọc một bản riêng. Lý do
// không phải lịch sự mà là chống rò: trang Đơn hàng gần như cả công ty vào
// được, nên một bản dùng chung sẽ dạy lễ tân phần việc của kế toán. Đọc
// hướng dẫn nhiều trang rồi tự ghép lại là cách dựng được bức tranh vận hành
// toàn công ty mà không ai cấp cho.
//
// Nhóm theo công việc chứ không theo mã vai vì hai lý do: mã vai có tới ~30 tổ
// hợp (vai × phòng), và hai người khác vai nhưng làm cùng một việc thì cần
// đúng một bài hướng dẫn — tách ra chỉ đẻ bản sao lệch nhau.

import type { Staff } from "@/lib/authv2/store";
import { isShiftLeadTitle } from "@/lib/auth/page-access";

export type Audience =
  | "reception"
  | "sale"
  | "photo"
  | "makeup"
  | "assistant"
  | "retouch"
  | "cskh"
  | "marketing"
  | "accounting"
  | "qc"
  | "lead"
  | "exec";

const DEPT_AUDIENCE: Record<string, Audience> = {
  reception: "reception",
  sales: "sale",
  sale: "sale",
  photo: "photo",
  photo_idphoto: "photo",
  photo_profile: "photo",
  photo_corporate: "photo",
  makeup: "makeup",
  // Trợ lý Studio (QĐ 0164) — nhóm RIÊNG, không dồn vào "photo". Họ đứng cạnh
  // thợ nhưng việc khác hẳn, và khối bài của thợ tả việc của thợ; phát nhầm là
  // đúng cái rào này sinh ra để chặn. Mã phòng ở hồ sơ nhân sự là
  // `studio_assistant` (gạch dưới), KHÁC `studioAssistant` của sổ biểu giá —
  // tra sai thì hàm rơi về [], im lặng y như lúc chưa khai.
  studio_assistant: "assistant",
  retouch: "retouch",
  cskh: "cskh",
  marketing: "marketing",
  accounting: "accounting",
};

/**
 * Nhóm hướng dẫn của `staff`, xếp từ hẹp tới rộng.
 *
 * Trả về MẢNG vì một người có thể đứng ở hai chỗ cùng lúc: trưởng phòng hậu kỳ
 * vừa làm việc hậu kỳ vừa điều hành, nên họ đọc cả hai khối. Nhân viên hậu kỳ
 * chỉ nhận `["retouch"]` — đó chính là chỗ rào lại.
 *
 * Thứ tự có ý nghĩa: khối hẹp hiện trước, vì việc tay chân hằng ngày mới là
 * thứ người ta mở hướng dẫn để tra.
 */
export function audiencesOf(staff: Staff | null | undefined): Audience[] {
  if (!staff) return [];
  const role = (staff.role || "").toLowerCase().trim();
  const dept = (staff.department || "").toLowerCase().trim();

  // Giám đốc & quản trị: đọc bản điều hành. KHÔNG gộp thêm khối phòng ban —
  // họ cần biết trang dùng để làm gì và quyết cái gì, không cần bài hướng dẫn
  // thao tác của từng phòng.
  if (role === "admin" || role === "director") return ["exec"];

  // QC gác chất lượng phòng mình: đọc bài QC, kèm bài của phòng đó để hiểu
  // người bị chấm đang làm gì.
  if (role === "qc") {
    const deptAud = DEPT_AUDIENCE[dept];
    return deptAud ? ["qc", deptAud] : ["qc"];
  }

  // Vai chuỗi: điều hành cơ sở / cụm. Không có phòng cụ thể.
  //
  // `shift_lead` (QĐ 0209) vào cùng nhóm, và PHẢI được nêu tên ở đây: vai ấy
  // gắn với CA chứ không gắn với phòng, nên người mang nó vẫn giữ mã phòng cũ
  // trên hồ sơ. Thiếu dòng này thì hàm rơi xuống cuối và phát cho họ bài của
  // PHÒNG đó — một trưởng ca mang mã "reception" đọc trọn bài lễ tân, trong
  // khi họ còn không vào được trang lễ tân. Đúng thứ ranh giới thông tin cấm.
  if (role === "branch_manager" || role === "area_manager" || role === "shift_lead") {
    return ["lead"];
  }

  // Vai `accounting` mang nghề kế toán ngay trong tên vai, không cần tra phòng.
  if (role === "accounting") return ["accounting"];
  // Vai `sale` là mã chuẩn của kinh doanh (hồ sơ cũ ghi staff + phòng sale).
  if (role === "sale") return ["sale"];

  const deptAud = DEPT_AUDIENCE[dept];

  // Trưởng phòng: bài của phòng mình TRƯỚC, rồi tới bài điều hành.
  if (role === "manager") return deptAud ? [deptAud, "lead"] : ["lead"];

  // Trưởng ca theo CHỨC DANH HRM (QĐ 0425): vẫn làm việc của phòng mình, thêm
  // vận hành cơ sở như Trưởng cửa hàng ⇒ đọc như trưởng phòng.
  if (isShiftLeadTitle(staff)) return deptAud ? [deptAud, "lead"] : ["lead"];

  // Nhân viên thường: đúng một bài của phòng mình. Chưa gán phòng thì không
  // khớp khối nào — họ vẫn đọc được phần "trang này để làm gì", đó là chủ ý:
  // thà thiếu còn hơn phát nhầm bài của phòng khác.
  return deptAud ? [deptAud] : [];
}
